<template>
    <vx-input
        ref="input"
        v-if="isTouchDevice"
        v-mask="mask"
        v-bind="$attrs"
        :icon="icon_x"
        :value="timeString"
        placeholder="--:--"
        :clearable="false"
        type="time"
        class="time-input"
        @focus="inputActive = true"
        @blur="inputActive = false"
        @input="setFromInput"
    />
    <vx-dropdown
        v-else
        v-bind="$attrs"
        ref="dropdown"
        menu-class="time-dropdown-menu"
        justify="start"
        align="bottom"
        :open-on-click="true"
    >
        <vx-input
            ref="input"
            v-if="is24hr"
            v-mask="mask"
            v-bind="$attrs"
            icon="icon-clock"
            placeholder="##:##"
            :value="timeString"
            :clearable="false"
            :type="isTouchDevice ? 'time' : 'text'"
            class="time-input"
            @focus="inputActive = true"
            @blur="inputActive = false"
            @input="setFromInput"
        />

        <vx-unit-input
            v-else
            v-model="timeString"
            v-bind="$attrs"
            icon="icon-clock"
            :clearable="false"
            :unit-options="amPmOptions"
            :unit.sync="amPm"
        />
        <template v-slot:menu>
            <div class="p-2">
                <slot name="top" />
                <div class="flex justify-between">
                    <slot name="left" />

                    <date-picker
                        ref="calendar"
                        mode="time"
                        :value="date"
                        :is-dark="isThemeDark"
                        :is24hr="is24hr"
                        :validHours="validHours"
                        :validMinutes="validMinutes"
                        :minute-increment="minuteIncrement"
                        @input="setFromPicker"
                    />
                    <slot name="right" />
                </div>
                <slot name="bottom"> </slot>
            </div>
        </template>
    </vx-dropdown>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { DatePicker } from 'v-calendar'

const MINUTE_MASK = [/[0-5]/, /[0-9]/]

export default {
    name: 'vx-time-input',
    components: {
        DatePicker
    },
    props: {
        value: {
            type: [Date, String],
            default: new Date()
        },
        closeOnSelect: {
            type: Boolean,
            default: false
        },
        is24hr: {
            type: Boolean,
            default: false
        },
        validHours: {
            type: Array
        },
        validMinutes: {
            type: Array
        },
        minuteIncrement: {
            type: Number
        },
        hideIcon: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        inputActive: {
            handler: function (newV, oldV) {
                if (newV) {
                    this.selectAllText()
                    return
                }
                if (this.date) {
                    this.timeString = this.createTimeString(this.date)
                }
            },
            immediate: true
        },
        date: {
            handler: function (newV, oldV) {
                this.timeString = this.createTimeString(newV)
            },
            immediate: true
        }
    },
    data() {
        return {
            timeString: null,
            mask: '##:##',
            inputActive: false,
            amPmOptions: ['AM', 'PM']
        }
    },
    computed: {
        ...mapState({
            isTouchDevice: 'is_touch_device'
        }),
        ...mapGetters({
            isThemeDark: 'theme/isThemeDark'
        }),
        icon_x() {
            if (this.hideIcon) {
                return null
            }
            return 'icon-clock'
        },
        hours() {
            return this.getHours(this.value)
        },
        minutes() {
            return this.getMinutes(this.value)
        },
        amPm: {
            get() {
                if (!this.date) {
                    return ''
                }
                return this.date.getAmPm()
            },
            set(v) {
                var newDateTime = this.date ? new Date(this.date) : new Date()
                if (this.amPm == 'PM' && v == 'AM') {
                    newDateTime = newDateTime.addHours(-12)
                } else if (this.amPm == 'AM' && v == 'PM') {
                    newDateTime = newDateTime.addHours(12)
                } else {
                    return
                }
                this.date = newDateTime
            }
        },
        date: {
            get() {
                if (!this.value) {
                    return null
                }
                return new Date(this.value)
            },
            set(val) {
                this.$emit('input', val)

                if (this.closeOnSelect) {
                    this.close()
                }
            }
        }
    },
    methods: {
        onFocus() {
            this.inputType = 'time'
            this.inputActive = true
        },
        selectAllText() {
            this.$refs.input.select()
        },
        createTimeString(date) {
            if (!date) {
                return '00:00'
            }
            return `${this.getHours(date)}:${this.getMinutes(date)}`
        },
        getHours(date) {
            if (date) {
                return this.standardizeHours(date).zeroPad(2)
            }
            if (this.is24hr) {
                return '00'
            }
            return '12'
        },
        standardizeHours(date) {
            var hours = date.getHours()
            if (this.is24hr) {
                return hours
            }
            if (hours == 0) {
                return 12
            }
            if (hours > 12) {
                return hours - 12
            }
            return hours
        },
        getMinutes(date) {
            if (!date) {
                return '00'
            }
            return date.getMinutes().zeroPad(2)
        },
        getMask(value) {
            if (this.is24hr) {
                return this.get24HrMask(value)
            }
            return this.get12HrMask(value)
        },
        get12HrMask(value) {
            var hours = [/[0-1]/, /[0-9]/]
            if (value && value.charAt(0) === '1') {
                hours[1] = /[0-2]/
            }
            return [...hours, ':', ...MINUTE_MASK]
        },
        get24HrMask(value) {
            var hours = [/[0-2]/, /[0-9]/]
            if (value && value.charAt(0) === '2') {
                hours[1] = /[0-3]/
            }
            return [...hours, ':', ...MINUTE_MASK]
        },
        setFromPicker(newDateTime) {
            if (this.inputActive) {
                return
            }
            this.mask = null
            this.date = newDateTime

            if (!newDateTime) {
                this.timeString = null
                return
            }
            var hours = this.getHours(newDateTime)
            var mins = this.getMinutes(newDateTime)
            var current = this.timeString

            // prevents overwriting while a person is typing
            if (current && current.length > 0 && current.length < 5) {
                return
            }
            this.timeString = `${hours}:${mins}`
        },
        checkIsSelected() {
            if (!this.inputActive) {
                return false
            }
            var selectedText = null
            if (window.getSelection) {
                selectedText = window.getSelection()
            } else if (document.getSelection) {
                selectedText = document.getSelection()
            } else if (document.selection) {
                selectedText = document.selection.createRange().text
            }
            return !!selectedText
        },
        validAgainstMask(timeStr) {
            if (!timeStr) {
                return true
            }
            var mask = this.getMask(timeStr)
            if (timeStr[0] && !mask[0].test(timeStr[0])) {
                return false
            }
            if (timeStr[1] && !mask[1].test(timeStr[1])) {
                return false
            }
            return true
        },
        setFromInput(newTime) {
            var wasSelected = this.checkIsSelected()
            if (!this.validAgainstMask(newTime)) {
                if (wasSelected) {
                    setTimeout(this.selectAllText, 5)
                }
                return
            }
            if (newTime == null) {
                this.mask = null
                this.timeString = null
                return
            }

            if (!this.inputActive) {
                return
            }
            this.mask = this.getMask(newTime)
            this.timeString = newTime

            var date = this.date ? new Date(this.date) : new Date()
            if (!newTime) {
                date.setHours(0)
                date.setMinutes(0)
                date.setSeconds(0)

                this.date = date
                return
            }
            var currentHours = this.getHours(this.date)
            var currentMins = this.getMinutes(this.date)

            var hours1 = newTime[0] || currentHours[0] || 0
            var hours2 = newTime[1] || currentHours[1] || 0
            var hours = Number(`${hours1}${hours2}`)

            var mins1 = newTime[3] || currentMins[0] || 0
            var mins2 = newTime[4] || currentMins[1] || 0
            var mins = Number(`${mins1}${mins2}`)

            date.setHours(hours)
            date.setMinutes(mins)
            date.setSeconds(0)

            this.date = date
        },
        close() {
            if (this.$refs.dropdown) {
                setTimeout(this.$refs.dropdown.close, 50)
            }
        },
        tryClear(e, visible) {
            if (!visible) {
                return
            }
            e.preventDefault()
            this.date = null
        }
    },
    mounted() {
        this.timeString = this.createTimeString(this.value)
    }
}
</script>

<style lang="scss">
.time-dropdown-menu {
    margin-top: -19px;
    .vc-container {
        background-color: transparent;
        border: none !important;
    }
}
.time-input {
    input {
        -webkit-appearance: none;
    }
}
</style>
